// The Import files preview map, as data: the open file only (every file when none is open, so
// files never get mixed up on the map) — its lines (all drawn red, dashed when left out; a
// hike from waypoints as the solid straight line it saves through its waypoints; a snapped day
// as its snapped line in red over the original in grey, with a legend), the waypoints it keeps
// as pins, the peaks a bagged-peaks CSV matched, and the points the camera frames. Pure, so
// ImportPreviewMap.vue only draws it.
// Ported from Android's `ImportMapModel.kt`.

import type { ImportLine, ImportPoint } from './importTypes';
import { fromWaypointsDay, keptWaypoints, plannedLines, recordingTracks, thinned, waypointsToAdd } from './importPayloads';
import { isSingle, willImport, type ImportScreenState } from './importScreenState';
import { appendAll, isRecordedTrack } from './importRules';
import { snapDays, snapStatus, usableSnap } from './importSnap';

/** How a line is drawn: by what its file becomes. */
export type ImportMapLineStyle = 'recording' | 'planned' | 'skipped';

export interface ImportMapLine {
  points: ImportPoint[];
  style: ImportMapLineStyle;
  /** Drawn dashed: the file is left out. */
  dashed: boolean;
  /** The line as the file drew it, under its snapped version: grey and dashed, labelled "Original line". */
  original?: boolean;
}

/** A peak a bagged-peaks CSV matched: `toBag` when importing will bag it, else already bagged or left out. */
export interface ImportMapPeak {
  point: ImportPoint;
  toBag: boolean;
}

/**
 * The map's key while Snap to paths is on: `matching` while a ticked file is still being
 * matched, `shown` once a snapped line is drawn (red "Snapped to paths", grey "Original line").
 */
export type ImportMapLegend = 'hidden' | 'matching' | 'shown';

export interface ImportMapModel {
  lines: ImportMapLine[];
  pins: ImportPoint[];
  peaks: ImportMapPeak[];
  /** The open file's lines and pins on a several-file screen; empty when none is open. */
  focusPoints: ImportPoint[];
  legend: ImportMapLegend;
}

/** Per line, at most this many points reach the map — plenty for a small preview. */
export const MAP_PREVIEW_MAX_POINTS = 400;

/** At most this many waypoint pins in total; a big waypoint file doesn't need every pin. */
export const MAP_PREVIEW_MAX_PINS = 200;

/** Every drawn point. */
export const allPoints = (m: ImportMapModel): ImportPoint[] => [...m.lines.flatMap(l => l.points), ...m.pins, ...m.peaks.map(p => p.point)];

/** What the camera frames: the open file, or everything when none is open. */
export const cameraPoints = (m: ImportMapModel): ImportPoint[] => (m.focusPoints.length ? m.focusPoints : allPoints(m));

export const isEmptyMap = (m: ImportMapModel): boolean => !m.lines.length && !m.pins.length && !m.peaks.length;

/** The rows of a bagged-peaks CSV the server matched to a peak with a position. */
function matchedPeaks(entry: ImportScreenState['entries'][number], importing: boolean): ImportMapPeak[] {
  const out: ImportMapPeak[] = [];
  for (const row of entry.peakCheck?.rows ?? []) {
    const lat = row.amenity?.latitude;
    const lon = row.amenity?.longitude;
    if (lat == null || lon == null) continue;
    out.push({ point: { lat, lon }, toBag: importing && row.status === 'NEW' });
  }
  return out;
}

export function mapModel(s: ImportScreenState): ImportMapModel {
  const lines: ImportMapLine[] = [];
  const pins: ImportPoint[] = [];
  const peaks: ImportMapPeak[] = [];
  const focused: ImportPoint[] = [];
  const focus = isSingle(s) ? null : s.openId;
  let matching = false;
  let snappedDrawn = false;
  for (const entry of s.entries) {
    const p = entry.preview;
    if (!p || p.error) continue;
    // Only the open file is drawn, so its lines can't be confused with another file's.
    if (focus !== null && focus !== entry.id) continue;
    const linesBefore = lines.length;
    const pinsBefore = pins.length;
    const importing = willImport(entry);
    const choice = entry.settings.choice;
    const peaksBefore = peaks.length;
    appendAll(peaks, matchedPeaks(entry, importing));
    // Recordings and a planned hike draws each line as what it becomes.
    const styleOf = (line: ImportLine): ImportMapLineStyle =>
      !importing ? 'skipped' : choice === 'recording' || (choice === 'both' && isRecordedTrack(line)) ? 'recording' : 'planned';
    const drawn =
      choice === 'recording'
        ? recordingTracks(p)
        : choice === 'planned'
          ? plannedLines(p, entry.settings)
          : p.lines.filter(l => l.duplicateOf === null);
    const stored = s.snaps?.[entry.id];
    if (importing && snapStatus(entry, stored) === 'matching') matching = true;
    const snap = importing ? usableSnap(entry, stored) : null;
    if (snap) {
      // Day by day, as it'll be saved: a snapped day's line in red over the original in grey;
      // a day that didn't snap keeps its original line, in red, because that's what imports.
      snapDays(entry).forEach((day, i) => {
        const match = snap.days[i];
        if (day.points.length < 2) return;
        const original = thinned(day.points, MAP_PREVIEW_MAX_POINTS);
        if (match?.ok) {
          lines.push({ points: original, style: 'planned', dashed: false, original: true });
          lines.push({ points: thinned(match.line, MAP_PREVIEW_MAX_POINTS), style: 'planned', dashed: false });
          snappedDrawn = true;
        } else {
          lines.push({ points: original, style: 'planned', dashed: false });
        }
      });
    } else {
      drawn.forEach(line => {
        const style = styleOf(line);
        line.segments
          .filter(seg => seg.length >= 2)
          .forEach(seg => lines.push({ points: thinned(seg, MAP_PREVIEW_MAX_POINTS), style, dashed: style === 'skipped' }));
      });
    }
    // A hike from waypoints is drawn as it's saved: straight through the waypoints, in file order.
    if (choice === 'fromWaypoints') {
      const through = fromWaypointsDay(p).points;
      if (through.length >= 2) lines.push({ points: thinned(through, MAP_PREVIEW_MAX_POINTS), style: importing ? 'planned' : 'skipped', dashed: false });
    }
    if (importing && choice !== 'recording') {
      appendAll(pins, waypointsToAdd(p, entry.settings).map(w => w.point));
    } else if (!p.lines.length) {
      appendAll(pins, keptWaypoints(p).map(w => w.point));
    }
    if (entry.id === focus) {
      appendAll(focused, lines.slice(linesBefore).flatMap(l => l.points));
      appendAll(focused, pins.slice(pinsBefore));
      appendAll(focused, peaks.slice(peaksBefore).map(peak => peak.point));
      // A file drawn as nothing (no waypoints kept, say) still has its lines to show.
      if (!focused.length) appendAll(focused, p.lines.flatMap(l => l.segments.flat()));
    }
  }
  return {
    lines,
    pins: thinned(pins, MAP_PREVIEW_MAX_PINS),
    peaks,
    focusPoints: thinned(focused, MAP_PREVIEW_MAX_POINTS),
    legend: matching ? 'matching' : snappedDrawn ? 'shown' : 'hidden'
  };
}

/** [[west, south], [east, north]] around `points`, or null when there are none. */
export function boundsOf(points: ImportPoint[]): [[number, number], [number, number]] | null {
  if (!points.length) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const p of points) {
    west = Math.min(west, p.lon);
    east = Math.max(east, p.lon);
    south = Math.min(south, p.lat);
    north = Math.max(north, p.lat);
  }
  return [[west, south], [east, north]];
}
